import { getRef, putRef } from './OfflineDb';
import ConnectivityService from './ConnectivityService';

const API_URL = process.env.REACT_APP_API_URL || '';

async function fetchAndCache(type: string, url: string, options?: RequestInit): Promise<any> {
    if (ConnectivityService.isOnline) {
        try {
            const res = await fetch(url, { credentials: 'include', ...options });
            if (res.ok) {
                const data = await res.json();
                await putRef(type, data);
                return data;
            }
        } catch (e) {
            console.warn(`[RefData] Fetch failed for ${type}, falling back to cache`, e);
        }
    }
    // Fallback to cached data
    const cached = await getRef(type);
    if (cached) return cached;
    throw new Error(`No cached data for ${type} and offline`);
}

export const ReferenceDataService = {
    async getProducts(propertyId: string) {
        return fetchAndCache('products', `${API_URL}/Product?propertyId=${propertyId}`);
    },

    // These endpoints read the property from the JWT claim, so there is no /all/{propertyId}
    // route — that URL 404'd on every app start and left the offline category cache empty.
    async getCategories(_propertyId: string) {
        return fetchAndCache('categories', `${API_URL}/ProductCategory`);
    },

    // get-all-active, matching what TableOrder fetches. Plain /Combination returns inactive ones
    // too, and both write the same 'combinations' cache key — so whichever landed last decided
    // what the POS showed offline.
    async getCombinations(_propertyId: string) {
        return fetchAndCache('combinations', `${API_URL}/Combination/get-all-active`);
    },

    async getFloors(propertyId: string) {
        return fetchAndCache('floors', `${API_URL}/Floor?propertyId=${propertyId}`);
    },

    async getFloor(floorId: string, propertyId: string) {
        // Try to get from cached floors first
        const floors = await this.getFloors(propertyId);
        if (Array.isArray(floors)) {
            const floor = floors.find((f: any) => f.id === floorId);
            if (floor) return floor;
        }
        return fetchAndCache(`floor_${floorId}`, `${API_URL}/Floor/${floorId}?propertyId=${propertyId}`);
    },

    async getPrinters(propertyId: string) {
        return fetchAndCache('printers', `${API_URL}/Printer?propertyId=${propertyId}`);
    },

    async getCompany(propertyId: string) {
        return fetchAndCache('company', `${API_URL}/Company/${propertyId}`);
    },

    async getPrintSettings(propertyId: string) {
        return fetchAndCache('printSettings', `${API_URL}/PrintSettings?propertyId=${propertyId}`);
    },

    async getReceiptTemplate() {
        if (ConnectivityService.isOnline) {
            try {
                const res = await fetch('https://tableview.blob.core.windows.net/receipttemplates/html-receipt.html');
                if (res.ok) {
                    const html = await res.text();
                    await putRef('receiptTemplate', html);
                    return html;
                }
            } catch (e) {
                console.warn('[RefData] Failed to fetch receipt template, using cache', e);
            }
        }
        const cached = await getRef('receiptTemplate');
        return cached || '';
    },

    async syncAll(propertyId: string) {
        if (!ConnectivityService.isOnline) return;
        console.log('[RefData] Syncing all reference data...');
        await Promise.allSettled([
            this.getProducts(propertyId),
            this.getCategories(propertyId),
            this.getCombinations(propertyId),
            this.getFloors(propertyId),
            this.getPrinters(propertyId),
            this.getCompany(propertyId),
            this.getPrintSettings(propertyId),
            this.getReceiptTemplate(),
        ]);
        console.log('[RefData] Sync complete');
    },
};

export default ReferenceDataService;
